import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, CheckCircle, AlertCircle, Copy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import authService from "@/services/authService";
import { getApiError } from "@/services/api";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [isLoading, setIsLoading] = useState(false);
  const [temporaryPassword, setTemporaryPassword] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("유효하지 않은 링크입니다.");
      return;
    }

    const confirmReset = async () => {
      setIsLoading(true);
      try {
        const result = await authService.confirmReset(token);
        setTemporaryPassword(result.temporary_password);
      } catch (err) {
        const apiError = getApiError(err);
        setError(apiError.message || "비밀번호 재설정에 실패했습니다.");
      } finally {
        setIsLoading(false);
      }
    };

    confirmReset();
  }, [token]);

  const handleCopy = async () => {
    if (!temporaryPassword) return;
    try {
      await navigator.clipboard.writeText(temporaryPassword);
    } catch {
      // HTTPS가 아닌 환경에서 clipboard API 미지원 시 fallback
      const textarea = document.createElement("textarea");
      textarea.value = temporaryPassword;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // 로딩 중
  if (isLoading) {
    return (
      <div className="text-center">
        <Loader2 className="mx-auto h-12 w-12 animate-spin text-muted-foreground" />
        <h2 className="mt-4 text-xl font-bold">비밀번호 재설정 중...</h2>
        <p className="mt-2 text-muted-foreground">잠시만 기다려 주세요.</p>
      </div>
    );
  }

  // 에러
  if (error) {
    return (
      <div className="text-center">
        <AlertCircle className="mx-auto h-12 w-12 text-destructive" />
        <h2 className="mt-4 text-xl font-bold">재설정 실패</h2>
        <p className="mt-2 text-muted-foreground">{error}</p>
        <Link
          to="/forgot-password"
          className="mt-6 inline-flex items-center text-primary hover:underline"
        >
          비밀번호 찾기 다시 요청하기
        </Link>
      </div>
    );
  }

  // 성공
  if (temporaryPassword) {
    return (
      <div className="text-center">
        <CheckCircle className="mx-auto h-12 w-12 text-green-500" />
        <h2 className="mt-4 text-xl font-bold">비밀번호가 재설정되었습니다</h2>
        <p className="mt-2 text-muted-foreground">
          아래 임시 비밀번호로 로그인해 주세요.
        </p>

        <div className="mt-6 rounded-lg border bg-muted/50 p-6">
          <p className="text-xs text-muted-foreground mb-2">임시 비밀번호</p>
          <div className="flex items-center justify-center gap-2">
            <code className="text-2xl font-bold tracking-wider">
              {temporaryPassword}
            </code>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={handleCopy}
            >
              {copied ? (
                <Check className="h-4 w-4 text-green-500" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        <p className="mt-4 text-sm text-amber-600">
          로그인 후 반드시 비밀번호를 변경해 주세요.
        </p>

        <Link to="/login">
          <Button className="mt-6 w-full">
            <ArrowLeft className="mr-2 h-4 w-4" />
            로그인하기
          </Button>
        </Link>
      </div>
    );
  }

  return null;
}
